import React, { Component } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Dropdown, Select, Ref } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import { MODES, MODE_OPTIONS } from '../../../constants/optionLists'
import Chip from '../../elementals/chip'
import InfoPill from '../../elementals/infoPill'
import { ColorNames } from '../../../constants/colorVars'
import styles from '../styles.scss'
const cx = classnames.bind(styles)

/**
 * DropdownInputField
 *  The selected data value (input.value) is shaped differently depending on use as follows:
 *    - Single select (multiple = false): data corresponds to the selected item value (string, number or bool)
 *    - Multiple select: array of the values selected (array of string, number or bool)
 *    - Multiple with "modeSelect" option (which is another dropdown for selecting a separate
 *  value - ALL/INCLUDE/EXCLUDE - in addition to the primary download value). In this case, since we have
 *  a complex data type, the data will be serialized as far as redux-form is concerned.
 *  The object serialized is shaped like this:
 *
 *  {
 *    mode: ('ALL', 'INCLUDE', 'EXCLUDE'),
 *    value: <array> in the case of multiple, <string, number or bool> in the case of single select.
 *  }
 */
class DropdownInputField extends Component {
  static propTypes = {
    fieldProps: PropTypes.object.isRequired,
    showError: PropTypes.bool,
    compact: PropTypes.bool,
    forceRefreshOnLoad: PropTypes.bool,
    showMultiSelectOptionsBelow: PropTypes.bool,
  }

  static defaultProps = {
    compact: false,
    showMultiSelectOptionsBelow: false,
  }

  constructor(props) {
    super(props)
    const { fieldProps } = props
    if (!fieldProps.multiple && fieldProps.modeSelect)
      console.warn(
        'WARNING: using modeSelect without multiple is not fully tested.'
      )

    this.state = {
      modeValue: '',
      dropdownValue: '',
      multiOptions: fieldProps.options || [],
      searchQuery: '',
      forcedRefresh: fieldProps.forceRefreshOnLoad,
      focused: false,
      ...this.deserializeValue(_.get(fieldProps, 'value')),
    }
  }

  /**
   * If modeSelect, will convert object to string so that redux-form can deal with it.
   */
  serializeValue = value => {
    const { modeSelect } = this.props.fieldProps
    return modeSelect
      ? JSON.stringify({
          mode: this.state.modeValue,
          value,
        })
      : value
  }

  /**
   * Deals with bogus initial values.  Semantic multi Dropdown needs an array, doesn't like undefined or string.
   */
  cleanMultiInputData = value => {
    if (!value || (_.isArray(value) && typeof value[0] === 'undefined')) {
      value = []
    } else if (typeof value === 'string') {
      value = [value]
    }

    return value
  }

  /**
   * Deserializes value if modeSelect is enabled.  Otherwise just uses value.
   * Returns object with modeValue and dropdownValue, irrespective of modeSelect enabled or not
   */
  deserializeValue = value => {
    const { modeSelect, multiple } = this.props.fieldProps
    let dropdownValue = value
    let modeValue = ''

    if (modeSelect) {
      let parsedValue
      try {
        parsedValue = !_.isUndefined(value) && JSON.parse(value)
      } catch (e) {
        // eat this error
      }
      dropdownValue = _.get(parsedValue, 'value', value)
      modeValue = _.get(parsedValue, 'mode', MODES.ALL)
    }
    dropdownValue = multiple
      ? this.cleanMultiInputData(dropdownValue)
      : dropdownValue
    return { modeValue, dropdownValue }
  }

  /**
   * NOTE: also called directly from onModeChange to force update on input value.
   */
  onChange = (e, data) => {
    const { onChange, multiple } = this.props.fieldProps
    const value = data ? data.value : this.state.dropdownValue
    onChange && onChange(this.serializeValue(value))
    !multiple && this.setState({ searchQuery: '' })
  }

  onBlur = (e, data) => {
    /**
     * Commented this out because it was causing a bug where clicking the option causes an immediate blur in the latest
     * version of Chrome - which in turn would basically onBlur the field before the value was set.
     *
     *   const { onBlur } = this.props.fieldProps
     *   const value = data && data.value
     *   onBlur && onBlur(this.serializeValue(value))
     *
     * TBH - not sure why we had to do this to begin with, but even if you remove this whole onBlur method altogether,
     * it still fails with the latest version of Chrome.
     *
     * NOTE: this effectively disables onBlur for dropdownInputs :O - but at least it works.
     */
    this.setState({ focused: false, searchQuery: '' })
    if (this.state.innerRef)
      _.defer(() => {
        // TODO: Need to debug this a little more. Been like this for years so I didn't feel confident changeing it
        // while upgrading linters.
        /* eslint-disable-next-line react/no-direct-mutation-state */
        this.state.innerRef.scrollTop = 0
      })
  }

  onFocus = (e, data) => {
    this.setState({ focused: true })
  }

  onModeChange = (e, data) => {
    const { multiple } = this.props.fieldProps
    if (data.value === MODES.ALL) {
      this.setState(
        {
          modeValue: data.value,
          dropdownValue: multiple ? [] : '',
        },
        this.onChange
      )
    } else {
      this.setState({ modeValue: data.value }, this.onChange)
    }
  }

  /**
   * Here we intercept the onSearchChange coming from semantic just so that we can
   * keep track of the searchQuery and use it later when forcing a re-query.
   */
  onSearchChange = (e, data) => {
    this.setState({ searchQuery: data.searchQuery })
    this.props.fieldProps.onSearchChange &&
      this.props.fieldProps.onSearchChange(e, data)
  }

  /**
   * Given a list of options, add new options created from values to ensure our list has currently selected options
   * @param {array} currentOptions
   * @param {array} values
   */
  mergeValuesOntoOptions(options, values) {
    const newOptions = [...options]
    _.each(values, val => {
      if (!_.find(newOptions, { value: val })) {
        newOptions.unshift({ value: val, text: val })
      }
    })
    return newOptions
  }

  /**
   * Async suggestions are tricky because we have to ensure that the selected values are included,
   * However, when we remove a selected value, we need to potentially have that value show back
   * up in the options list (depending if the removed value meets the search query criteria).
   *
   * This is also tricky, as we can't use the selected value data (as it's just the value) to stick
   * into the list, as the list will most likely be showing additional fields.  Therefore,
   * when we remove, we force a re-query the list by calling onSearchChange with the current query.
   */
  handleAsyncSuggestions = newValue => {
    const { dropdownValue: oldValue } = this.state
    const { options: curOptions } = this.props.fieldProps
    if (!_.isEmpty(_.difference(oldValue, newValue))) {
      this.setState({ multiOptions: [], forcedRefresh: true })
    } else {
      const newOptions = this.mergeValuesOntoOptions(curOptions, newValue) // merge values in
      this.setState({ multiOptions: newOptions })
    }
  }

  componentDidUpdate = prevProps => {
    const { forcedRefresh } = this.state
    const { value: prevValue, options: prevOptions } = prevProps.fieldProps
    const {
      value: curValue,
      options: curOptions,
      multiple,
      asyncSuggestions,
    } = this.props.fieldProps
    const curParsed = this.deserializeValue(curValue)

    const valuesChanged = curValue !== prevValue
    const optionsChanged = !_.isEqual(curOptions, prevOptions)

    if (valuesChanged || optionsChanged || forcedRefresh) {
      if (multiple && asyncSuggestions) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ forcedRefresh: false }, () => {
          this.handleAsyncSuggestions(curParsed.dropdownValue)
        })
      }

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(curParsed) // sets modeValue and dropdownValue
    }
  }

  render() {
    const {
      modeValue,
      dropdownValue,
      multiOptions,
      searchQuery,
      focused,
      innerRef,
    } = this.state
    const { fieldProps, showError } = this.props
    const {
      elmId,
      placeholder,
      modeSelect,
      disabled,
      multiple,
      options,
      asyncSuggestions,
      onSearchChange, // eslint-disable-line
      forceRefreshOnLoad, // eslint-disable-line
      compact,
      className,
      customModeOptions,
      detachedMenu,
      useChipLabels,
      showMultiSelectOptionsBelow,
      chipColor,
      ...rest
    } = fieldProps // eslint-disable-line

    // overwrite the input props that came from semantic
    const newInput = {
      onBlur: this.onBlur,
      onChange: this.onChange,
      value: dropdownValue,
      onFocus: this.onFocus,
    }

    const newPlaceholder =
      // eslint-disable-next-line no-nested-ternary
      modeValue === MODES.ALL
        ? 'All selected...'
        : useChipLabels && _.isArray(dropdownValue) && dropdownValue.length > 0
        ? ''
        : placeholder || 'Select'
    const newOptions = (multiple ? multiOptions : options) || []

    // semantic-ui-react's DropdownItem forwards unknown props (incl. `data-*`)
    // to the rendered `<div role="option">`, so this lands on each option.
    const optionsWithElmIds = newOptions.map(opt => ({
      ...opt,
      'data-elm-id':
        opt['data-elm-id'] ??
        `${elmId}_option_${_.snakeCase(String(opt.value))}`,
    }))

    const effectiveChipColor = chipColor || undefined

    const chipRenderLabel = (item, index, defaultProps) => (
      <Chip
        elmId={`${elmId}-chip-${index}`}
        type='input'
        size='small'
        color={effectiveChipColor}
        selectionStyle='light'
        fontWeight='normal'
        content={item.text}
        onClick={() =>
          defaultProps.onRemove({ stopPropagation: () => {} }, defaultProps)
        }
      />
    )

    // When showMultiSelectOptionsBelow is true, suppress inline labels
    const hiddenRenderLabel = () => ({
      content: '',
      style: { display: 'none' },
    })

    // If useChipLabels, use chip renderer; otherwise pass through whatever rest has
    // If showMultiSelectOptionsBelow, hide inline labels entirely
    // eslint-disable-next-line no-nested-ternary
    const renderLabelProp = showMultiSelectOptionsBelow
      ? { renderLabel: hiddenRenderLabel }
      : useChipLabels
      ? { renderLabel: chipRenderLabel }
      : {}

    /**
     * This is the dropdown's search filtering.  When async, we already have a filtered list, so we want
     * to effectively disable the normal string searching it does when the user types.  To do this we
     * change the filtering function to identity, which will return each filter value back - so essentially
     * keeping all values in the (fetched) option list.
     */
    const searchFilter = asyncSuggestions ? _.identity : true
    const newClassName = classnames({
      compact,
      [className]: true,
    })

    // Add mode-specific class to the dropdown wrapper
    const dropdownWrapperClass = cx('dropdown', {
      'with-mode-select': modeSelect,
      'detached-menu': detachedMenu,
    })

    return (
      <div className={dropdownWrapperClass}>
        {modeSelect && (
          <Select
            data-elm-id={`${elmId}_mode_select`}
            disabled={!!disabled}
            options={customModeOptions || MODE_OPTIONS}
            onChange={this.onModeChange}
            className={`${cx('mode-select')} ${newClassName}`}
            value={modeValue}
            label={`${elmId}_mode_select_label`}
          />
        )}
        {/* <Ref innerRef={node => this.setState({ innerRef: node })}> */}
        <Ref
          innerRef={node => {
            if (!innerRef && node) {
              this.setState({ innerRef: node })
            }
          }}
        >
          <Dropdown
            search={searchFilter}
            onSearchChange={this.onSearchChange}
            searchQuery={searchQuery}
            multiple={multiple}
            options={optionsWithElmIds}
            selectOnBlur={false}
            selection
            placeholder={newPlaceholder}
            error={showError}
            data-elm-id={`${elmId}_select`}
            label={`${elmId}_select_label`}
            disabled={!!disabled || modeValue === MODES.ALL}
            {...rest}
            {...renderLabelProp}
            {...newInput}
            className={classnames(
              cx('dropdown-field', { focused }),
              compact ? 'compact' : '',
              className
            )}
          />
        </Ref>
        {showMultiSelectOptionsBelow &&
          multiple &&
          _.isArray(dropdownValue) &&
          dropdownValue.length > 0 && (
            <div
              className={cx('multi-select-below')}
              data-elm-id={`${elmId}_selected_chips`}
            >
              {dropdownValue.map((val, index) => {
                const option = _.find(newOptions, { value: val })
                return (
                  <Chip
                    key={val}
                    elmId={`${elmId}-chip-below-${index}`}
                    type='input'
                    size={compact ? 'medium' : 'large'}
                    selectionStyle='light'
                    color={effectiveChipColor}
                    disabled={!!disabled}
                    content={option ? option.text : val}
                    onClick={() => {
                      const newValue = dropdownValue.filter(v => v !== val)
                      this.setState({ dropdownValue: newValue }, () => {
                        const { onChange } = this.props.fieldProps
                        onChange && onChange(this.serializeValue(newValue))
                      })
                    }}
                  />
                )
              })}
            </div>
          )}
      </div>
    )
  }
}

const DropdownInputFieldWrapped = formFieldWrapper((fieldProps, showError) => {
  return <DropdownInputField fieldProps={fieldProps} showError={showError} />
})

DropdownInputFieldWrapped.propTypes = {
  options: PropTypes.array.isRequired,
  modeSelect: PropTypes.bool,
  customModeOptions: PropTypes.array,
  asyncSuggestions: PropTypes.bool,
  detachedMenu: PropTypes.bool,
  useChipLabels: PropTypes.bool,
  showMultiSelectOptionsBelow: PropTypes.bool,
  chipColor: PropTypes.string,
  ...commonPropTypes,
}

/**
 * Utils for user of this field to serialize/deserialize data
 * Assumes multiple select mode is in use
 */
DropdownInputFieldWrapped.serializeValue = obj => JSON.stringify(obj || {})

DropdownInputFieldWrapped.deserializeValue = s => {
  let parsed = { mode: '', value: [] }
  try {
    parsed = JSON.parse(s)
  } catch (e) {
    // eat this error
  }
  return parsed
}

DropdownInputFieldWrapped.MODES = MODES
DropdownInputFieldWrapped.MODE_OPTIONS = MODE_OPTIONS

const chipViewModeRenderer = options => value => {
  if (!value || !_.isArray(value) || value.length === 0) return null
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '3px',
      }}
    >
      {value.map(val => {
        const option = _.find(options, { value: val })
        return (
          <InfoPill
            key={val}
            content={option ? option.text : val}
            size='small'
            backgroundColor={ColorNames.InfoExtraLight}
            foregroundColor={ColorNames.Info}
          />
        )
      })}
    </div>
  )
}

const DropdownField = props => {
  const {
    useChipLabels,
    options,
    viewModeRenderer,
    showMultiSelectOptionsBelow,
    chipColor,
    ...rest
  } = props
  const effectiveViewModeRenderer =
    useChipLabels && !viewModeRenderer
      ? chipViewModeRenderer(options)
      : viewModeRenderer
  return (
    <DropdownInputFieldWrapped
      {...rest}
      options={options}
      useChipLabels={useChipLabels}
      showMultiSelectOptionsBelow={showMultiSelectOptionsBelow}
      chipColor={chipColor}
      viewModeRenderer={effectiveViewModeRenderer}
    />
  )
}

// Preserve static methods
DropdownField.serializeValue = DropdownInputFieldWrapped.serializeValue
DropdownField.deserializeValue = DropdownInputFieldWrapped.deserializeValue
DropdownField.MODES = DropdownInputFieldWrapped.MODES
DropdownField.MODE_OPTIONS = DropdownInputFieldWrapped.MODE_OPTIONS

export default DropdownField
